// Schaltungseditor: SVG-Arbeitsfläche, Palette, Live-Simulation, Wahrheitstabelle.
// Bedienung ausschließlich über Pointer Events, damit Finger, Stift und Maus gleich funktionieren.

import { GATE_ARITY, GATE_OUTPUTS, check, gateCounts, inputRows, liveValues, sourceGate } from "./sim.ts";
import type { Bit, Circuit, CheckResult, Gate, Task } from "./sim.ts";
import { reducedMotion } from "./confetti.ts";
import { ICON } from "./icons.ts";
import { assignTrunks, planDetour } from "./route.ts";
import type { Detour, Rect } from "./route.ts";
import { store } from "./store.ts";

const W = 1000;
const GATE_W = 70;
const GATE_H = 90;
const SNAP = 10;
const PIN_RADIUS = 34; // Fangradius beim Verbinden (SVG-Einheiten)
const TAP_SLOP = 6; // Pixel, ab denen eine Berührung als Ziehen gilt

const NEGATED = new Set(["NOT", "NAND", "NOR", "XNOR"]);
const SYMBOL: Record<string, string> = {
  NOT: "1", AND: "&", NAND: "&", OR: "≥1", NOR: "≥1", XOR: "=1", XNOR: "=1", HA: "HA", VA: "VA",
};
const LABEL: Record<string, string> = { HA: "Halbaddierer", VA: "Volladdierer" };

type Pin = { kind: "src" | "sink"; id: string };
type Point = { x: number; y: number };
type Selection = { kind: "gate" | "wire"; id: string } | null;

type Drag =
  | { kind: "move"; gateId: string; start: Point; offset: Point; moved: boolean; client: Point }
  | { kind: "wire"; from: Pin; at: Point; client: Point; moved: boolean }
  | { kind: "new"; type: string; at: Point | null };

const esc = (s: string) => s.replace(/[&<>"]/g, c => `&#${c.charCodeAt(0)};`);
const snap = (v: number) => Math.round(v / SNAP) * SNAP;

export function gateSymbolSvg(type: string): string {
  const neg = NEGATED.has(type);
  const outs = GATE_OUTPUTS[type];
  return `<svg viewBox="-24 -8 128 106" aria-hidden="true">
    <rect x="0" y="0" width="${GATE_W}" height="${GATE_H}" rx="3" class="gate-body"/>
    <text x="${outs ? 28 : 35}" y="54" class="gate-sym">${esc(SYMBOL[type])}</text>
    ${neg ? `<circle cx="78" cy="45" r="8" class="gate-bubble"/>` : ""}
    ${(outs ?? []).map((name, k) => `<text x="${GATE_W - 6}" y="${k ? 70 : 30}" class="gate-out">${esc(name)}</text>`).join("")}
  </svg>`;
}

export class Editor {
  private circuit: Circuit;
  private env: Record<string, Bit>;
  private selection: Selection = null;
  private drag: Drag | null = null;
  private undoStack: string[] = [];
  private visited = new Set<number>();
  private result: CheckResult | null = null;
  private hintsShown: number;
  private readonly H: number;
  private readonly svg: SVGSVGElement;
  private readonly layer: SVGGElement;
  private readonly palette: HTMLElement;
  private readonly side: HTMLElement;

  private readonly root: HTMLElement;
  private readonly task: Task;
  private readonly consts: string[]; // Konstanten-Quellen "0"/"1" unterhalb der Eingänge

  private readonly next: { id: string; titel: string } | null;
  private celebrateTimer: number | null = null;
  private popStars = false;

  constructor(root: HTMLElement, task: Task, next: { id: string; titel: string } | null = null) {
    this.next = next;
    this.root = root;
    this.task = task;
    this.consts = (task.konstanten ?? []).map(String);
    const n = Math.max(task.eingaenge.length + this.consts.length, task.ausgaenge.length);
    this.H = Math.max(520, 150 * n + 80);
    this.circuit = store.circuit(task.id) ?? { gates: [], wires: [] };
    this.env = Object.fromEntries(task.eingaenge.map(name => [name, 0 as Bit]));
    this.visited.add(0);
    this.hintsShown = store.hints(task.id);

    root.innerHTML = `
      <section class="task-head">
        <a class="back" href="#">← Aufgaben</a>
        <h1>${esc(task.titel)}${task.exkurs ? ` <span class="exkurs-badge">Exkurs</span>` : ""}</h1>
        <p class="barometer" hidden></p>
        ${task.text ? `<p>${esc(task.text)}</p>` : ""}
      </section>
      <div class="workbench${task.eingaenge.length + 2 * task.ausgaenge.length > 8 ? " wide" : ""}">
        <aside class="palette" aria-label="Bauteile"></aside>
        <div class="canvas-wrap">
          <svg class="canvas" viewBox="0 0 ${W} ${this.H}" role="application"
               aria-label="Arbeitsfläche"><g class="layer"></g></svg>
          <div class="toolbar">
            <button data-act="undo" title="Rückgängig">${ICON.undo}<span>Rückgängig</span></button>
            <button data-act="delete" title="Auswahl löschen">${ICON.trash}<span>Löschen</span></button>
            <button data-act="clear" title="Alles entfernen">Neu</button>
            <span class="spacer"></span>
            <button data-act="hint" class="secondary"></button>
            <button data-act="check" class="primary">Prüfen</button>
          </div>
        </div>
        <aside class="side"></aside>
      </div>`;
    this.svg = root.querySelector("svg.canvas")!;
    this.layer = this.svg.querySelector("g.layer")!;
    this.palette = root.querySelector(".palette")!;
    this.side = root.querySelector(".side")!;

    this.svg.addEventListener("pointerdown", e => this.onSvgDown(e));
    this.svg.addEventListener("pointermove", e => this.onMove(e));
    this.svg.addEventListener("pointerup", e => this.onUp(e));
    this.svg.addEventListener("pointercancel", () => { this.drag = null; this.render(); });
    // Scrollen per Touch nur verhindern, wenn pointerdown (kommt vor touchstart) ein Ziehen begonnen hat
    this.svg.addEventListener("touchstart", e => { if (this.drag) e.preventDefault(); }, { passive: false });
    root.querySelector(".toolbar")!.addEventListener("click", e => {
      const act = (e.target as HTMLElement).closest<HTMLElement>("[data-act]")?.dataset.act;
      if (act) this.action(act);
    });
    this.side.addEventListener("click", e => {
      const row = (e.target as HTMLElement).closest<HTMLElement>("[data-row]");
      if (row) this.setRow(Number(row.dataset.row));
    });
    document.addEventListener("keydown", this.onKey);
    this.renderPalette();
    this.render();
  }

  destroy(): void {
    this.stopCelebration();
    document.removeEventListener("keydown", this.onKey);
  }

  // ---------------------------------------------------------------- Geometrie

  private inputPos(i: number): Point {
    const n = this.task.eingaenge.length + this.consts.length;
    return { x: 100, y: Math.round((this.H * (i + 1)) / (n + 1)) };
  }

  /** Eingangsbelegung plus feste Konstanten (für die Live-Simulation). */
  private withConsts(env: Record<string, Bit>): Record<string, Bit> {
    return { ...env, ...Object.fromEntries(this.consts.map(c => [c, Number(c) as Bit])) };
  }

  private outputPos(i: number): Point {
    return { x: W - 100, y: Math.round((this.H * (i + 1)) / (this.task.ausgaenge.length + 1)) };
  }

  private gate(id: string): Gate | undefined {
    return this.circuit.gates.find(g => g.id === id);
  }

  private arity(g: Gate): number {
    return GATE_ARITY[g.type];
  }

  private gateInPin(g: Gate, k: number): Point {
    const n = this.arity(g);
    const dy = n === 1 ? 45 : n === 3 ? [20, 45, 70][k] : k === 0 ? 25 : 65;
    return { x: g.x! - 20, y: g.y! + dy };
  }

  /** Namen der Ausgänge: "" bei einfachen Gattern, sonst z. B. ["S", "C"]. */
  private outNames(g: Gate): string[] {
    return GATE_OUTPUTS[g.type] ?? [""];
  }

  private gateOutPin(g: Gate, name: string): Point {
    const k = this.outNames(g).indexOf(name);
    const dy = this.outNames(g).length === 1 ? 45 : k === 0 ? 25 : 65;
    return { x: g.x! + 100, y: g.y! + dy };
  }

  private srcId(g: Gate, name: string): string {
    return name ? `${g.id}.${name}` : g.id;
  }

  private pinPos(pin: Pin): Point | null {
    if (pin.kind === "src") {
      const i = this.task.eingaenge.indexOf(pin.id);
      if (i >= 0) return this.inputPos(i);
      const c = this.consts.indexOf(pin.id);
      if (c >= 0) return this.inputPos(this.task.eingaenge.length + c);
      const g = this.gate(sourceGate(pin.id));
      return g ? this.gateOutPin(g, pin.id.split(".")[1] ?? "") : null;
    }
    const j = this.task.ausgaenge.indexOf(pin.id);
    if (j >= 0) return this.outputPos(j);
    const [gid, k] = pin.id.split(".");
    const g = this.gate(gid);
    return g ? this.gateInPin(g, Number(k)) : null;
  }

  private allPins(): Pin[] {
    const pins: Pin[] = [
      ...this.task.eingaenge.map(id => ({ kind: "src" as const, id })),
      ...this.consts.map(id => ({ kind: "src" as const, id })),
      ...this.task.ausgaenge.map(id => ({ kind: "sink" as const, id })),
    ];
    for (const g of this.circuit.gates) {
      for (const name of this.outNames(g)) pins.push({ kind: "src", id: this.srcId(g, name) });
      for (let k = 0; k < this.arity(g); k++) pins.push({ kind: "sink", id: `${g.id}.${k}` });
    }
    return pins;
  }

  private nearestPin(p: Point, kind: Pin["kind"], exceptGate?: string): Pin | null {
    let best: Pin | null = null;
    let bestD = PIN_RADIUS;
    for (const pin of this.allPins()) {
      if (pin.kind !== kind || (exceptGate && pin.id.split(".")[0] === exceptGate)) continue;
      const q = this.pinPos(pin)!;
      const d = Math.hypot(q.x - p.x, q.y - p.y);
      if (d < bestD) { bestD = d; best = pin; }
    }
    return best;
  }

  private toSvg(e: { clientX: number; clientY: number }): Point {
    const m = this.svg.getScreenCTM();
    if (!m) return { x: 0, y: 0 };
    const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(m.inverse());
    return { x: p.x, y: p.y };
  }

  private clampGate(p: Point): Point {
    return {
      x: snap(Math.min(Math.max(p.x, 150), W - 260)),
      y: snap(Math.min(Math.max(p.y, 20), this.H - GATE_H - 20)),
    };
  }

  private freeSpot(): Point {
    for (let x = 260; x <= W - 300; x += 140) {
      for (let y = 40; y <= this.H - GATE_H - 20; y += 130) {
        const busy = this.circuit.gates.some(g => Math.abs(g.x! - x) < 120 && Math.abs(g.y! - y) < 110);
        if (!busy) return { x, y };
      }
    }
    return { x: W / 2 - GATE_W / 2, y: this.H / 2 - GATE_H / 2 };
  }

  // ---------------------------------------------------------------- Änderungen

  private mutate(fn: () => void): void {
    this.stopCelebration();
    this.undoStack.push(JSON.stringify(this.circuit));
    if (this.undoStack.length > 100) this.undoStack.shift();
    fn();
    this.result = null;
    store.saveCircuit(this.task.id, this.circuit);
    this.renderPalette();
    this.render();
  }

  private remaining(type: string): number {
    const limit = this.task.bauteile[type];
    if (limit === "*") return Infinity;
    return limit - (gateCounts(this.circuit).get(type) ?? 0);
  }

  private addGate(type: string, at: Point): void {
    if (this.remaining(type) <= 0) return;
    let n = 1;
    while (this.gate(`g${n}`)) n++;
    const id = `g${n}`;
    this.mutate(() => this.circuit.gates.push({ id, type, ...this.clampGate(at) }));
    this.selection = null;
  }

  private deleteGate(id: string): void {
    this.mutate(() => {
      this.circuit.gates = this.circuit.gates.filter(g => g.id !== id);
      this.circuit.wires = this.circuit.wires.filter(w => sourceGate(w.from) !== id && w.to.split(".")[0] !== id);
    });
  }

  private connect(a: Pin, b: Pin): void {
    const src = a.kind === "src" ? a : b;
    const sink = a.kind === "sink" ? a : b;
    if (src.kind === sink.kind || sourceGate(src.id) === sink.id.split(".")[0]) return;
    this.mutate(() => {
      // Ein Eingang hat genau eine Quelle: eine neue Leitung ersetzt die alte.
      this.circuit.wires = this.circuit.wires.filter(w => w.to !== sink.id);
      this.circuit.wires.push({ from: src.id, to: sink.id });
    });
  }

  private action(act: string): void {
    switch (act) {
      case "undo": {
        const prev = this.undoStack.pop();
        if (!prev) return;
        this.circuit = JSON.parse(prev);
        this.selection = null;
        this.result = null;
        store.saveCircuit(this.task.id, this.circuit);
        this.renderPalette();
        this.render();
        return;
      }
      case "delete": return this.deleteSelection();
      case "clear":
        if (this.circuit.gates.length || this.circuit.wires.length) {
          this.mutate(() => { this.circuit = { gates: [], wires: [] }; });
        }
        return;
      case "hint":
        if (this.hintsShown < this.task.hinweise.length) {
          this.hintsShown++;
          store.saveHints(this.task.id, this.hintsShown);
          this.render();
        }
        return;
      case "check": return this.runCheck();
    }
  }

  private deleteSelection(): void {
    const sel = this.selection;
    if (!sel) return;
    this.selection = null;
    if (sel.kind === "gate") this.deleteGate(sel.id);
    else this.mutate(() => { this.circuit.wires = this.circuit.wires.filter(w => w.to !== sel.id); });
  }

  private setRow(i: number): void {
    this.stopCelebration();
    const row = inputRows(this.task.eingaenge)[i];
    if (!row) return;
    this.env = { ...row };
    this.visited.add(i);
    this.render();
  }

  private rowIndex(): number {
    return this.task.eingaenge.reduce((acc, name) => acc * 2 + this.env[name], 0);
  }

  private runCheck(): void {
    this.result = check(this.task, this.circuit);
    inputRows(this.task.eingaenge).forEach((_, i) => this.visited.add(i));
    this.root.dispatchEvent(new CustomEvent("checked", {
      detail: { result: this.result, circuit: this.circuit, hints: this.hintsShown },
    }));
    if (this.result.solved) {
      const stars = 1 + (this.result.par_erreicht ? 1 : 0)
        + (this.result.par_erreicht && this.hintsShown === 0 ? 1 : 0);
      const before = store.stars();
      store.saveStars(this.task.id, stars);
      this.root.dispatchEvent(new CustomEvent("solved", { detail: { stars, before, result: this.result } }));
      this.popStars = true;
      this.render();
      this.popStars = false;
      this.celebrate();
      return;
    }
    this.render();
  }

  /** Erfolgsmoment: Das Signal „fließt“ durch alle Eingangskombinationen nacheinander. */
  private celebrate(): void {
    this.stopCelebration();
    if (reducedMotion()) return;
    const rows = inputRows(this.task.eingaenge);
    const saved = this.env;
    let i = 0;
    this.svg.classList.add("flowing");
    const step = () => {
      if (i >= rows.length) {
        this.stopCelebration();
        this.env = saved;
        this.render();
        return;
      }
      this.env = { ...rows[i++] };
      this.render();
    };
    step();
    this.celebrateTimer = window.setInterval(step, 1100);
  }

  private stopCelebration(): void {
    if (this.celebrateTimer !== null) window.clearInterval(this.celebrateTimer);
    this.celebrateTimer = null;
    this.svg?.classList.remove("flowing");
  }

  // ---------------------------------------------------------------- Pointer

  private onKey = (e: KeyboardEvent): void => {
    if ((e.target as HTMLElement).closest("input, textarea")) return;
    if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); this.deleteSelection(); }
    if (e.key === "z" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); this.action("undo"); }
    if (e.key === "Escape") { this.selection = null; this.render(); }
  };

  private onSvgDown(e: PointerEvent): void {
    if (this.drag || !e.isPrimary) return;
    this.stopCelebration();
    const el = (e.target as Element).closest<SVGElement>("[data-pin],[data-gate],[data-wire],[data-input]");
    const p = this.toSvg(e);
    const client = { x: e.clientX, y: e.clientY };
    this.svg.setPointerCapture(e.pointerId);

    if (el?.dataset.input) {
      const name = el.dataset.input;
      this.env = { ...this.env, [name]: (1 - this.env[name]) as Bit };
      this.visited.add(this.rowIndex());
      this.render();
      return;
    }
    if (el?.dataset.pin) {
      const [kind, id] = el.dataset.pin.split(":") as [Pin["kind"], string];
      this.drag = { kind: "wire", from: { kind, id }, at: p, client, moved: false };
      this.selection = null;
    } else if (el?.dataset.gate) {
      const g = this.gate(el.dataset.gate)!;
      this.drag = { kind: "move", gateId: g.id, start: { x: g.x!, y: g.y! },
                    offset: { x: p.x - g.x!, y: p.y - g.y! }, moved: false, client };
    } else if (el?.dataset.wire) {
      this.selection = { kind: "wire", id: el.dataset.wire };
    } else {
      this.selection = null;
    }
    this.render();
  }

  private onMove(e: PointerEvent): void {
    const d = this.drag;
    if (!d || d.kind === "new") return;
    const p = this.toSvg(e);
    if (!d.moved && Math.hypot(e.clientX - d.client.x, e.clientY - d.client.y) < TAP_SLOP) return;
    d.moved = true;
    if (d.kind === "move") {
      const g = this.gate(d.gateId)!;
      Object.assign(g, this.clampGate({ x: p.x - d.offset.x, y: p.y - d.offset.y }));
      this.palette.classList.toggle("trash-hot", this.overPalette(e));
    } else {
      d.at = p;
    }
    this.render();
  }

  private onUp(e: PointerEvent): void {
    const d = this.drag;
    if (!d || d.kind === "new") return;
    this.drag = null;
    this.palette.classList.remove("trash-hot");
    if (d.kind === "move") {
      const g = this.gate(d.gateId)!;
      if (!d.moved) {
        const same = this.selection?.kind === "gate" && this.selection.id === g.id;
        this.selection = same ? null : { kind: "gate", id: g.id };
      } else if (this.overPalette(e)) {
        Object.assign(g, d.start);
        this.deleteGate(g.id);
        return;
      } else {
        const end = { x: g.x!, y: g.y! };
        Object.assign(g, d.start); // Ausgangszustand für Undo merken
        if (end.x !== d.start.x || end.y !== d.start.y) this.mutate(() => Object.assign(g, end));
      }
    } else if (d.moved) {
      const want = d.from.kind === "src" ? "sink" : "src";
      const target = this.nearestPin(this.toSvg(e), want, d.from.id.split(".")[0]);
      if (target) this.connect(d.from, target);
    } else if (d.from.kind === "sink") {
      // Tippen auf einen belegten Eingang wählt dessen Leitung aus
      if (this.circuit.wires.some(w => w.to === d.from.id)) this.selection = { kind: "wire", id: d.from.id };
    }
    this.render();
  }

  private overPalette(e: { clientX: number; clientY: number }): boolean {
    const r = this.palette.getBoundingClientRect();
    return e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
  }

  private onPaletteDown(e: PointerEvent, type: string, btn: HTMLElement): void {
    if (this.remaining(type) <= 0 || !e.isPrimary) return;
    e.preventDefault();
    btn.setPointerCapture(e.pointerId);
    const start = { x: e.clientX, y: e.clientY };
    const drag: Drag = { kind: "new", type, at: null };
    this.drag = drag;
    const move = (ev: PointerEvent) => {
      if (!drag.at && Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < TAP_SLOP) return;
      const r = this.svg.getBoundingClientRect();
      const inside = ev.clientX >= r.left && ev.clientX <= r.right && ev.clientY >= r.top && ev.clientY <= r.bottom;
      const p = this.toSvg(ev);
      drag.at = inside ? this.clampGate({ x: p.x - GATE_W / 2, y: p.y - GATE_H / 2 }) : { x: -999, y: -999 };
      this.render();
    };
    const up = () => {
      btn.removeEventListener("pointermove", move);
      btn.removeEventListener("pointerup", up);
      btn.removeEventListener("pointercancel", up);
      this.drag = null;
      if (!drag.at) this.addGate(type, this.freeSpot()); // nur getippt
      else if (drag.at.x > -999) this.addGate(type, drag.at);
      this.render();
    };
    btn.addEventListener("pointermove", move);
    btn.addEventListener("pointerup", up);
    btn.addEventListener("pointercancel", up);
  }

  // ---------------------------------------------------------------- Darstellung

  private renderPalette(): void {
    this.palette.innerHTML = `<div class="palette-title">Bauteile</div>` +
      Object.keys(this.task.bauteile).map(type => {
        const rest = this.remaining(type);
        const label = rest === Infinity ? "" : `<span class="count">${rest}×</span>`;
        return `<button class="part" data-type="${type}" ${rest <= 0 ? "disabled" : ""}
                  title="${LABEL[type] ?? type} -- antippen oder auf die Fläche ziehen">
                  ${gateSymbolSvg(type)}<span class="part-name">${type}</span>${label}</button>`;
      }).join("") + `<div class="trash-hint">Hierher ziehen zum Löschen</div>`;
    this.palette.querySelectorAll<HTMLElement>("button.part").forEach(btn =>
      btn.addEventListener("pointerdown", e => this.onPaletteDown(e, btn.dataset.type!, btn)));
  }

  private render(): void {
    const values = liveValues(this.circuit, this.withConsts(this.env));
    const bad = new Set((this.result?.problems ?? []).map(p => p.ref ?? ""));
    const cls = (v: Bit | undefined) => (v === 1 ? "hi" : v === 0 ? "lo" : "unk");
    const parts: string[] = [];

    // Leitungen: gleiche Quelle teilt sich einen senkrechten Stamm (mit Abzweigpunkten);
    // Stämme verschiedener Quellen bekommen eigene Spuren (route.ts)
    const bySource = new Map<string, string[]>();
    for (const w of this.circuit.wires) bySource.set(w.from, [...(bySource.get(w.from) ?? []), w.to]);
    // Gatter als Hindernisse (inkl. Anschlussstummel); Leitungen, deren letztes waagerechtes
    // Stück ein fremdes Gatter schneiden würde, weichen darüber oder darunter aus
    const box = (g: Gate): Rect => ({ x0: g.x! - 22, y0: g.y! - 8, x1: g.x! + 102, y1: g.y! + GATE_H + 8 });
    type End = { id: string; p: Point; via: Detour | null; turn: Point }; // turn = Ende am Stamm
    const nets: { src: string; s: Point; ends: End[] }[] = [];
    for (const [src, sinks] of bySource) {
      const s = this.pinPos({ kind: "src", id: src });
      if (!s) continue;
      const ends: End[] = [];
      for (const id of sinks) {
        const p = this.pinPos({ kind: "sink", id });
        if (!p) continue;
        const [gid, k] = id.split(".");
        const obstacles = this.circuit.gates.filter(g => g.id !== sourceGate(src) && g.id !== gid).map(box);
        const via = planDetour(s, p, Number(k ?? 0), obstacles, this.H);
        ends.push({ id, p, via, turn: via ? { x: via.xt, y: via.yc } : p });
      }
      nets.push({ src, s, ends });
    }
    const trunks = assignTrunks(nets.map(n => ({ id: n.src, src: n.s, sinks: n.ends.map(e => e.turn) })));
    const dots: string[] = [];
    for (const { src, s, ends } of nets) {
      const xb = trunks.get(src) ?? s.x + 20;
      const c = cls(values.get(src));
      for (const { id, p, via } of ends) {
        const d = via
          ? `M${s.x} ${s.y}H${xb}V${via.yc}H${via.xt}V${p.y}H${p.x}`
          : `M${s.x} ${s.y}H${xb}V${p.y}H${p.x}`;
        const sel = this.selection?.kind === "wire" && this.selection.id === id ? " selected" : "";
        parts.push(`<path d="${d}" class="wire-hit" data-wire="${id}"/>`,
                   `<path d="${d}" class="wire ${c}${sel}"/>`);
      }
      if (ends.length > 1) {
        const ys = [s.y, ...ends.map(e => e.turn.y)];
        const lo = Math.min(...ys), hi = Math.max(...ys);
        for (const y of new Set(ys)) {
          const edges = (y === s.y ? 1 : 0) + ends.filter(e => e.turn.y === y).length
            + (y > lo ? 1 : 0) + (y < hi ? 1 : 0);
          if (edges >= 3) dots.push(`<circle cx="${xb}" cy="${y}" r="5" class="dot ${c}"/>`);
        }
      }
    }
    parts.push(...dots);

    // Gatter
    for (const g of this.circuit.gates) {
      const sel = this.selection?.kind === "gate" && this.selection.id === g.id ? " selected" : "";
      const err = bad.has(g.id) || bad.has(g.type) ? " error" : "";
      const neg = NEGATED.has(g.type);
      const stubs = Array.from({ length: this.arity(g) }, (_, k) => {
        const y = this.gateInPin(g, k).y - g.y!;
        return `<line x1="-20" y1="${y}" x2="0" y2="${y}" class="stub"/>`;
      }).join("");
      const outs = this.outNames(g);
      const outStubs = outs.map(name => {
        const y = this.gateOutPin(g, name).y - g.y!;
        return `<line x1="${neg ? 86 : GATE_W}" y1="${y}" x2="100" y2="${y}" class="stub"/>`
          + (name ? `<text x="${GATE_W - 6}" y="${y + 5}" class="gate-out">${esc(name)}</text>` : "");
      }).join("");
      parts.push(`<g class="gate${sel}${err}" data-gate="${g.id}" transform="translate(${g.x} ${g.y})">
        <rect x="-4" y="-22" width="${GATE_W + 8}" height="${GATE_H + 26}" class="gate-hit"/>
        ${stubs}
        <rect width="${GATE_W}" height="${GATE_H}" rx="3" class="gate-body"/>
        ${outStubs}
        ${neg ? `<circle cx="78" cy="45" r="8" class="gate-bubble"/>` : ""}
        <text x="${outs.length > 1 ? 28 : 35}" y="54" class="gate-sym">${esc(SYMBOL[g.type])}</text>
        <text x="35" y="-7" class="gate-label">${LABEL[g.type] ?? g.type}</text>
      </g>`);
    }

    // Ein- und Ausgänge
    this.task.eingaenge.forEach((name, i) => {
      const p = this.inputPos(i);
      const v = this.env[name];
      parts.push(`<g class="terminal input ${cls(v)}" data-input="${esc(name)}">
        <rect x="${p.x - 86}" y="${p.y - 24}" width="64" height="48" rx="10"/>
        <text x="${p.x - 54}" y="${p.y + 7}" class="term-val">${v}</text>
        <text x="${p.x - 54}" y="${p.y - 32}" class="term-name">${esc(name)}</text>
        <line x1="${p.x - 22}" y1="${p.y}" x2="${p.x}" y2="${p.y}" class="wire ${cls(v)}"/>
      </g>`);
    });
    this.consts.forEach((c, j) => {
      const p = this.inputPos(this.task.eingaenge.length + j);
      const v = Number(c) as Bit;
      parts.push(`<g class="terminal input const ${cls(v)}">
        <rect x="${p.x - 86}" y="${p.y - 24}" width="64" height="48" rx="10"/>
        <text x="${p.x - 54}" y="${p.y + 7}" class="term-val">${v}</text>
        <text x="${p.x - 54}" y="${p.y - 32}" class="term-name">Konstante</text>
        <line x1="${p.x - 22}" y1="${p.y}" x2="${p.x}" y2="${p.y}" class="wire ${cls(v)}"/>
      </g>`);
    });
    this.task.ausgaenge.forEach((name, j) => {
      const p = this.outputPos(j);
      const src = this.circuit.wires.find(w => w.to === name)?.from;
      const v = src === undefined ? undefined : values.get(src);
      const err = bad.has(name) ? " error" : "";
      parts.push(`<g class="terminal output ${cls(v)}${err}">
        <line x1="${p.x}" y1="${p.y}" x2="${p.x + 22}" y2="${p.y}" class="wire ${cls(v)}"/>
        <circle cx="${p.x + 54}" cy="${p.y}" r="26" class="lamp"/>
        <text x="${p.x + 54}" y="${p.y + 7}" class="term-val">${v ?? "?"}</text>
        <text x="${p.x + 54}" y="${p.y - 34}" class="term-name">${esc(name)}</text>
      </g>`);
    });

    // Pins (oben, mit großem Fangbereich)
    const d = this.drag;
    const candidate = d?.kind === "wire" && d.moved
      ? this.nearestPin(d.at, d.from.kind === "src" ? "sink" : "src", d.from.id.split(".")[0]) : null;
    for (const pin of this.allPins()) {
      const q = this.pinPos(pin)!;
      const hot = candidate && candidate.id === pin.id && candidate.kind === pin.kind ? " hot" : "";
      const err = bad.has(pin.id) && pin.kind === "sink" ? " error" : "";
      // Anschlusspunkt in Signalfarbe; offene Eingänge/Ausgänge als Ring („hier verbinden“)
      const driver = pin.kind === "sink" ? this.circuit.wires.find(w => w.to === pin.id)?.from : pin.id;
      const open = pin.kind === "sink" && driver === undefined;
      const v = driver === undefined ? undefined : values.get(driver);
      parts.push(`<g class="pin ${pin.kind}${hot}${err}" data-pin="${pin.kind}:${esc(pin.id)}">
        <circle cx="${q.x}" cy="${q.y}" r="22" class="pin-hit"/>
        <circle cx="${q.x}" cy="${q.y}" r="${open ? 6 : 5}" class="pin-dot ${open ? "open" : cls(v)}"/>
      </g>`);
    }

    // Laufende Interaktion
    if (d?.kind === "wire" && d.moved) {
      const s = this.pinPos(d.from)!;
      const t = candidate ? this.pinPos(candidate)! : d.at;
      parts.push(`<line x1="${s.x}" y1="${s.y}" x2="${t.x}" y2="${t.y}" class="rubber"/>`);
    }
    if (d?.kind === "new" && d.at && d.at.x > -999) {
      parts.push(`<g class="gate ghost" transform="translate(${d.at.x} ${d.at.y})">
        <rect width="${GATE_W}" height="${GATE_H}" rx="3" class="gate-body"/>
        <text x="35" y="54" class="gate-sym">${esc(SYMBOL[d.type])}</text></g>`);
    }

    this.layer.innerHTML = parts.join("");
    this.root.querySelector<HTMLButtonElement>('[data-act="delete"]')!.disabled = !this.selection;
    this.root.querySelector<HTMLButtonElement>('[data-act="undo"]')!.disabled = !this.undoStack.length;
    const hintBtn = this.root.querySelector<HTMLButtonElement>('[data-act="hint"]')!;
    const left = this.task.hinweise.length - this.hintsShown;
    hintBtn.innerHTML = `${ICON.bulb}<span>Hinweis${left > 0 ? ` (${left})` : ""}</span>`;
    hintBtn.disabled = left <= 0;
    hintBtn.hidden = this.task.hinweise.length === 0;
    this.renderSide(values);
  }

  private renderSide(values: Map<string, Bit>): void {
    const t = this.task;
    const rows = inputRows(t.eingaenge);
    const current = this.rowIndex();
    const wrong = new Set(this.result?.falsche_zeilen ?? []);
    const outSrc = t.ausgaenge.map(o => this.circuit.wires.find(w => w.to === o)?.from);

    const body = rows.map((env, i) => {
      const ist = i === current
        ? outSrc.map(s => (s === undefined ? undefined : values.get(s)))
        : outSrc.map(s => (s === undefined ? undefined : liveValues(this.circuit, this.withConsts(env)).get(s)));
      const shown = this.visited.has(i);
      const match = shown && ist.every((v, k) => v === t.tabelle[i][k]);
      const mark = !shown ? "" : match ? "ok" : ist.some(v => v === undefined) ? "unk" : "bad";
      const rowCls = [i === current ? "current" : "", wrong.has(i) ? "wrong" : "", mark].join(" ");
      return `<tr data-row="${i}" class="${rowCls}">
        ${t.eingaenge.map(n => `<td>${env[n]}</td>`).join("")}
        ${t.tabelle[i].map(v => `<td class="soll">${v}</td>`).join("")}
        ${ist.map(v => `<td class="ist">${shown ? (v ?? "?") : ""}</td>`).join("")}
        <td class="mark">${mark === "ok" ? "✓" : mark === "bad" ? "✗" : ""}</td>
      </tr>`;
    }).join("");

    const r = this.result;
    let feedback = "";
    if (r?.solved) {
      const stars = 1 + (r.par_erreicht ? 1 : 0) + (r.par_erreicht && this.hintsShown === 0 ? 1 : 0);
      const onward = this.next
        ? `<a class="next-task" href="#${this.next.id}">Nächste Aufgabe: ${esc(this.next.titel)} ${ICON.arrow}</a>`
        : `<a class="next-task" href="#">Zur Übersicht ${ICON.arrow}</a>`;
      feedback = `<div class="feedback success">
        <div class="stars${this.popStars ? " pop" : ""}">${"★".repeat(stars)}${"☆".repeat(3 - stars)}</div>
        <strong>Richtig!</strong> Ihre Schaltung erfüllt die Wahrheitstabelle
        mit ${r.gatter} Gatter${r.gatter === 1 ? "" : "n"}.
        ${t.par !== null && !r.par_erreicht ? `<br>Es geht auch mit ${t.par} Gattern -- schaffen Sie das?` : ""}
        ${r.par_erreicht && this.hintsShown > 0 ? "<br>Den dritten Stern gibt es, wenn Sie es ohne Hinweis schaffen." : ""}
        ${onward}
      </div>`;
    } else if (r) {
      const msgs = r.problems.length
        ? r.problems.map(p => `<li>${esc(p.message)}</li>`).join("")
        : `<li>In ${r.falsche_zeilen.length} Zeile${r.falsche_zeilen.length === 1 ? "" : "n"} stimmt
           der Ausgang nicht (rot markiert). Tippen Sie eine Zeile an, um sie nachzuverfolgen.</li>`;
      feedback = `<div class="feedback error"><strong>Noch nicht ganz.</strong><ul>${msgs}</ul></div>`;
    }

    const hints = t.hinweise.slice(0, this.hintsShown)
      .map(h => `<li>${esc(h)}</li>`).join("");

    this.side.innerHTML = `
      ${feedback}
      <table class="truth">
        <thead>
          <tr><th colspan="${t.eingaenge.length}">Eingänge</th>
              <th colspan="${t.ausgaenge.length}">Soll</th>
              <th colspan="${t.ausgaenge.length}">Ist</th><th></th></tr>
          <tr>${t.eingaenge.map(n => `<th>${esc(n)}</th>`).join("")}
              ${t.ausgaenge.map(n => `<th class="soll">${esc(n)}</th>`).join("")}
              ${t.ausgaenge.map(n => `<th class="ist">${esc(n)}</th>`).join("")}<th></th></tr>
        </thead>
        <tbody>${body}</tbody>
      </table>
      <p class="tip">Tippen Sie auf einen Eingang links oder auf eine Tabellenzeile, um die
        Eingangswerte zu ändern.</p>
      ${hints ? `<div class="hints"><strong>Hinweise</strong><ul>${hints}</ul></div>` : ""}`;
  }
}
